import { FixtureMap } from '../../services/fixtureClient';
import { domainError, ok, validationError } from './responses';

/**
 * Comms fixtures.
 *
 * The response shape follows Plan - Comms integration: ISO `createdDateTime`
 * (the frontend formats it), `attachments[] { fileName, href }`, and no
 * `patientDateOfBirth` — the DOB challenge compares server-side and this
 * payload is reachable before the challenge is passed.
 *
 * ENG-21798: re-pointed at the endpoints that actually shipped —
 * PatientPortal/V2/Application/PatientMessage/PatientMessageController.php:56,94,111.
 * Note `message` singular, and `authenticate` rather than `verify-identity`.
 *
 * Marking a message opened is a side effect of the authenticate call
 * (AuthenticatePatientMessageUsingDOBCommandHandler), not a separate endpoint.
 */
export const MESSAGE_THREAD_PATH = '/patient-portal/message/:messageId';
export const MESSAGE_VERIFY_PATH = '/patient-portal/message/:messageId/authenticate';
export const MESSAGE_REPLY_PATH = '/patient-portal/message/:messageId/reply';

const inboundMessage = {
  id: 'e1b7a0c2-1f4d-4c8a-9a3e-2f5d6c7b8a90',
  direction: 'INBOUND',
  title: 'About your recent blood test',
  createdDateTime: '2026-08-10T09:15:00Z',
  isStructuredCommunication: false,
  body: 'Your results are back and everything looks normal. Reply if you have questions.',
  attachments: [{ fileName: 'blood-test-results.pdf', href: '/patient/download/att-1' }],
};

const structuredReply = {
  id: 'c4d5e6f7-8a9b-4c0d-8e1f-2a3b4c5d6e7f',
  direction: 'OUTBOUND',
  title: 'Your answers',
  createdDateTime: '2026-08-11T14:02:00Z',
  isStructuredCommunication: true,
  responseDetails: [
    { question: 'How would you rate your pain?', answer: 'Moderate' },
    { question: 'Is the pain getting worse?', answer: 'No' },
  ],
  attachments: [],
};

export const messageThread = {
  messageId: 'e1b7a0c2-1f4d-4c8a-9a3e-2f5d6c7b8a90',
  firstName: 'John',
  canReply: true,
  replyWarning: {
    title: 'Do not use this to report an emergency',
    message: 'If you need urgent help, call 111. In an emergency, call 999.',
  },
  messages: [inboundMessage, structuredReply],
};

/** One message, no reply allowed — the view renders the single-message layout. */
export const singleMessageThread = {
  ...messageThread,
  canReply: false,
  replyWarning: null,
  messages: [inboundMessage],
};

export const messageFixtures: FixtureMap = {
  [`GET ${MESSAGE_THREAD_PATH}`]: ({ params }) =>
    ok({ ...messageThread, messageId: params.messageId }),

  [`POST ${MESSAGE_VERIFY_PATH}`]: ({ body }) => {
    const dateOfBirth = (body as { dateOfBirth?: string })?.dateOfBirth;

    if (!dateOfBirth) {
      return validationError({ dateOfBirth: ['Enter your date of birth'] });
    }

    // John Smith — the seeded local patient.
    if (dateOfBirth !== '1990-05-20') {
      // The endpoint answers 401 `date-of-birth-mismatch`; the catalogue keeps it inline.
      return domainError('date-of-birth-mismatch', { status: 401 });
    }

    return ok({ verified: true });
  },

  [`POST ${MESSAGE_REPLY_PATH}`]: ({ body }) => {
    const replyMessage = (body as { replyMessage?: string })?.replyMessage;

    if (!replyMessage) {
      return validationError({ replyMessage: ['Enter your reply'] });
    }

    return ok({ sent: true });
  },
};

/**
 * Failure fixtures for the terminal states, kept separate so the happy path
 * stays readable. Register one with `forceFixtureFailure` to exercise it.
 *
 * ENG-21798: codes and statuses are the endpoint's own — PatientMessageAccessChecker's
 * REASON_* constants, plus the `lockedUntil` sibling the lock response carries.
 */
export const LOCKED_UNTIL = '2026-08-18T17:45:00+01:00';

export const messageFailures = {
  expired: domainError('message-expired'),
  notFound: domainError('message-not-found', { status: 404 }),
  conversationClosed: domainError('message-conversation-closed'),
  locked: domainError('message-locked', { fields: { lockedUntil: LOCKED_UNTIL } }),
  dateOfBirthMismatch: domainError('date-of-birth-mismatch', { status: 401 }),
  sessionInvalid: domainError('user-session-invalid', { status: 401 }),
};
